/**
 * Os indicadores do painel da plataforma.
 *
 * Ficam fora dos adaptadores pela razão de sempre — os dois têm de chegar ao
 * mesmo número —, e porque é aqui que mora a única regra de negócio da tela:
 * **"em risco" é derivado, não gravado**. Clínica em risco é a que assinou e
 * parou de usar; guardar isso numa coluna criaria um campo que alguém esquece
 * de atualizar, e a clínica ficaria "em risco" para sempre depois de voltar.
 */

import type {
    AssinaturaDaClinica, ClinicaNaPlataforma, Fatura, FlagDeModulo, Indicador, Plano, SituacaoDaFatura,
} from './tipos';

/** Dias sem nenhum acesso para a clínica entrar na lista de risco. */
export const DIAS_DE_RISCO = 14;

export function emRisco(c: ClinicaNaPlataforma, hoje = new Date()): boolean {
    if (c.situacao === 'cancelada' || c.situacao === 'suspensa') return false;
    /* Quem nunca entrou é risco desde o primeiro dia: assinatura que não vira
       uso é churn que ainda não aconteceu. */
    if (!c.ultimoAcesso) return true;
    const dias = (hoje.getTime() - new Date(c.ultimoAcesso).getTime()) / 86400000;
    return dias > DIAS_DE_RISCO;
}

export function indicadoresDaPlataforma(clinicas: ClinicaNaPlataforma[]): Indicador[] {
    const ativas = clinicas.filter((c) => c.situacao === 'ativa');
    const trial = clinicas.filter((c) => c.situacao === 'trial');
    const inadimplentes = clinicas.filter((c) => c.situacao === 'inadimplente');
    const risco = clinicas.filter((c) => emRisco(c));
    const membros = clinicas.reduce((s, c) => s + c.membrosAtivos, 0);
    const agendamentos = clinicas.reduce((s, c) => s + c.agendamentos30d, 0);

    return [
        {
            id: 'ativas', rotulo: 'Clínicas ativas', valor: String(ativas.length),
            apoio: `${trial.length} em trial · ${inadimplentes.length} inadimplentes`, icone: 'building-2',
        },
        {
            id: 'risco', rotulo: 'Em risco', valor: String(risco.length),
            apoio: `sem acesso há mais de ${DIAS_DE_RISCO} dias`,
            tendencia: risco.length ? 'down' : 'up', icone: 'triangle-alert',
        },
        { id: 'membros', rotulo: 'Pessoas com acesso', valor: String(membros), apoio: 'somando as clínicas', icone: 'users' },
        /* Uso, não receita: faturamento exige um modelo de cobrança que esta
           instalação ainda não tem. Agendamento é o que prova que o produto
           está sendo usado. */
        {
            id: 'agendamentos', rotulo: 'Agendamentos em 30 dias', valor: String(agendamentos),
            apoio: 'em toda a base', icone: 'calendar-days',
        },
    ];
}

/**
 * A situação da fatura é DERIVADA, não gravada.
 *
 * Gravada, ela envelheceria sozinha à meia-noite: a fatura que hoje está aberta
 * amanhã está vencida, e nenhuma linha do banco muda por causa disso. Quem
 * grava situação precisa de alguém que a atualize todo dia — e é esse alguém
 * que costuma faltar.
 */
export function situacaoDaFatura(
    f: { pagoEm?: string; vencimento: string; estornadaEm?: string },
    hoje = new Date(),
): SituacaoDaFatura {
    if (f.estornadaEm) return 'estornada';
    if (f.pagoEm) return 'paga';
    const dia = hoje.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
    return f.vencimento < dia ? 'vencida' : 'aberta';
}

/**
 * Preço → desconto → créditos, do mais antigo ao mais novo.
 *
 * É a conta de `gerar_faturas` (migração `0092`), repetida aqui para a
 * demonstração chegar ao MESMO número e para a regra ser testada sem banco. O
 * arredondamento do desconto é o do Postgres para positivos: meio centavo sobe.
 */
export function valorDaFatura(
    precoCentavos: number,
    percentualDeDesconto: number | null,
    saldosDosCreditos: readonly number[],
): { descontoCentavos: number; creditoCentavos: number; valorCentavos: number; consumos: number[] } {
    const descontoCentavos = percentualDeDesconto ? Math.round((precoCentavos * percentualDeDesconto) / 100) : 0;
    const liquido = precoCentavos - descontoCentavos;
    let resta = liquido;
    const consumos = saldosDosCreditos.map((saldo) => {
        const usar = Math.min(resta, Math.max(0, saldo));
        resta -= usar;
        return usar;
    });
    const creditoCentavos = liquido - resta;
    return { descontoCentavos, creditoCentavos, valorCentavos: liquido - creditoCentavos, consumos };
}

/** A competência de hoje, no fuso da operação. */
export function competenciaDeHoje(hoje = new Date()): string {
    return hoje.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' }).slice(0, 7);
}

/** `2026-11` somado de 3 meses é `2027-02`. */
export function competenciaSomada(competencia: string, meses: number): string {
    const [ano, mes] = competencia.split('-').map(Number);
    const total = (ano ?? 0) * 12 + ((mes ?? 1) - 1) + meses;
    return `${Math.floor(total / 12)}-${String((total % 12) + 1).padStart(2, '0')}`;
}

/**
 * FNV-1a de 32 bits sobre os bytes UTF-8: o mesmo número para o mesmo texto, em
 * qualquer máquina — e o mesmo de `app.hash_estavel` no banco (migração `0095`).
 * Era sobre `charCodeAt`, que é UTF-16: igual ao banco só enquanto o texto fosse ASCII.
 */
export function hashEstavel(texto: string): number {
    let h = 0x811c9dc5;
    for (const byte of new TextEncoder().encode(texto)) {
        h ^= byte;
        h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
}

/**
 * O módulo está ligado para esta clínica? A precedência de `app.flag_ligada`
 * (migração `0093`): kill switch > exceção > rollout > padrão.
 *
 * O hash do rollout NÃO é o do servidor (lá é `hashtextextended`). Cada um é
 * estável — a mesma clínica não oscila —, mas a lista de quem cai dentro de 30%
 * na demonstração não é a mesma do servidor.
 */
export function flagLigadaPara(
    flag: Pick<FlagDeModulo, 'chave' | 'ativoPadrao' | 'excecoes' | 'rolloutPercentual' | 'desligadaEm'>,
    clinicaId: string,
): boolean {
    if (flag.desligadaEm) return false;
    const excecao = flag.excecoes.find((e) => e.clinicaId === clinicaId);
    if (excecao) return excecao.ativo;
    if (flag.rolloutPercentual !== undefined) return hashEstavel(`${flag.chave}:${clinicaId}`) % 100 < flag.rolloutPercentual;
    return flag.ativoPadrao;
}

/**
 * Receita recorrente das assinaturas que têm preço.
 *
 * Anual entra dividido por doze — somar um plano anual inteiro ao mês faria a
 * recorrente saltar em janeiro e desabar em fevereiro.
 */
export function recorrenteMensalCentavos(assinaturas: AssinaturaDaClinica[]): number {
    return assinaturas
        .filter((a) => a.precoCentavos !== undefined && !a.canceladaEm
            && (a.situacao === 'ativa' || a.situacao === 'inadimplente'))
        .reduce((s, a) => s + Math.round((a.precoCentavos ?? 0) / (a.ciclo === 'anual' ? 12 : 1)), 0);
}

const reais = (centavos: number) => `R$ ${(centavos / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

export function indicadoresDoFaturamento(
    planos: Plano[],
    assinaturas: AssinaturaDaClinica[],
    faturas: Fatura[],
): Indicador[] {
    const temPreco = planos.some((p) => p.precoCentavos !== undefined);
    const vencidas = faturas.filter((f) => f.situacao === 'vencida');
    const abertas = faturas.filter((f) => f.situacao === 'aberta');
    const recorrente = recorrenteMensalCentavos(assinaturas);

    return [
        {
            id: 'assinaturas', rotulo: 'Assinaturas', valor: String(assinaturas.filter((a) => !a.canceladaEm).length),
            apoio: `${assinaturas.filter((a) => a.canceladaEm).length} canceladas`, icone: 'layers',
        },
        /* Sem preço em plano nenhum, a recorrente não é zero: ela não existe.
           Um "R$ 0,00" aqui seria lido como base que não paga nada. */
        {
            id: 'recorrente', rotulo: 'Receita recorrente', valor: temPreco ? reais(recorrente) : '—',
            apoio: temPreco ? 'por mês, das assinaturas com preço' : 'nenhum plano tem preço definido',
            icone: 'circle-dollar-sign',
        },
        {
            id: 'vencidas', rotulo: 'Faturas vencidas', valor: String(vencidas.length),
            apoio: temPreco && vencidas.length ? reais(vencidas.reduce((s, f) => s + f.valorCentavos, 0)) : 'nada em atraso',
            tendencia: vencidas.length ? 'down' : 'up', icone: 'triangle-alert',
        },
        {
            id: 'abertas', rotulo: 'Faturas em aberto', valor: String(abertas.length),
            apoio: 'ainda dentro do prazo', icone: 'receipt',
        },
    ];
}
